import { useState } from "react"
import { FileText } from "lucide-react"
import { GoogleDriveFile } from "@/services/biblioService"
import { Button } from "@/components/ui/button"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"

interface FileButtonProps {
  /** The Google Drive file metadata to display. */
  file: GoogleDriveFile
}

/**
 * Opens the file's webViewLink in a new browser tab.
 *
 * @param file - The Google Drive file to open.
 */
function viewFile(file: GoogleDriveFile): void {
  if (file.webViewLink) {
    window.open(file.webViewLink, "_blank")
  }
}

/**
 * Renders a clickable button/thumbnail for a Google Drive file.
 *
 * If the file has a valid thumbnail, it displays the thumbnail image.
 * If the thumbnail fails to load or is missing, it falls back to a
 * styled button showing a file icon and the file name.
 *
 * @param props - Component props.
 * @param props.file - The Google Drive file metadata.
 * @returns The rendered file button element.
 */
export function FileButton({ file }: FileButtonProps): React.JSX.Element {
  const [thumbnailError, setThumbnailError] = useState<boolean>(false)

  /** Whether to show the thumbnail image or the fallback button. */
  const showThumbnail = file.thumbnailLink && !thumbnailError

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {showThumbnail ? (
          <button
            type="button"
            onClick={() => viewFile(file)}
            className="cursor-pointer rounded-md border border-border overflow-hidden hover:ring-2 hover:ring-primary/30 transition-shadow"
          >
            <img
              src={file.thumbnailLink}
              alt={file.name || "Downloadable file"}
              onError={() => setThumbnailError(true)}
              className="max-w-[166px] max-h-[204px] object-contain"
            />
          </button>
        ) : (
          <Button
            variant="outline"
            className="gap-2 h-auto py-2 px-3"
            onClick={() => viewFile(file)}
          >
            <FileText className="size-4 shrink-0" />
            <span className="truncate max-w-[200px]">
              {file.name || "Open file"}
            </span>
          </Button>
        )}
      </TooltipTrigger>
      <TooltipContent>
        {file.name ? `Open "${file.name}" in new tab` : "Open file in new tab"}
      </TooltipContent>
    </Tooltip>
  )
}
